import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useMount } from 'react-use'

import Image from '@/components/misc/Image'
import { Button } from '@/components/ui/button'
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useRecommendedPartnerModal } from '@/hooks/useRecommendedPartnerModal'
import { faTimes } from '@/lib/fontAwesomePro/light'
import { toSafeHttpUrl } from '@/lib/helpers/helperFunctions'
import { RecommendedPartnerStep } from '@/lib/services/recommendedPartnerService'
import AddToReferralPartnersButton from '@/pages/professionalNetworkRelationships/components/RecommendedPartnerModal/components/AddToReferralPartnersButton'
import RecommendedPartnerTabLinks from '@/pages/professionalNetworkRelationships/components/RecommendedPartnerModal/components/RecommendedPartnerTabLinks'
import WatchVideo from '@/pages/professionalNetworkRelationships/components/RecommendedPartnerModal/steps/1_WatchVideo/WatchVideo'
import WhereWeCanHelp from '@/pages/professionalNetworkRelationships/components/RecommendedPartnerModal/steps/2_WhereWeCanHelp/WhereWeCanHelp'
import WhatOurClientsSay from '@/pages/professionalNetworkRelationships/components/RecommendedPartnerModal/steps/3_WhatOurClientsSay/WhatOurClientsSay'
import WhatProfessionalsSay from '@/pages/professionalNetworkRelationships/components/RecommendedPartnerModal/steps/4_WhatProfessionalsSay/WhatProfessionalsSay'
import WorkingWithYou from '@/pages/professionalNetworkRelationships/components/RecommendedPartnerModal/steps/5_WorkingWithYou/WorkingWithYou'
import { useRecommendedPartnerQuery } from '@/pages/professionalNetworkRelationships/hooks/useRecommendedPartner'
import { useRecommendedPartnerSteps } from '@/pages/professionalNetworkRelationships/hooks/useRecommendedPartnerSteps'

const RECOMMENDED_PARTNER_STEPS: Record<
  RecommendedPartnerStep,
  React.ReactNode
> = {
  watchVideo: <WatchVideo />,
  whereWeCanHelp: <WhereWeCanHelp />,
  whatOurClientsSay: <WhatOurClientsSay />,
  whatProfessionalsSay: <WhatProfessionalsSay />,
  workingWithYou: <WorkingWithYou />,
}

const RecommendedPartnerModal: React.FC = () => {
  const modal = useRecommendedPartnerModal()
  const recommendedPartnerQuery = useRecommendedPartnerQuery()
  const recommendedPartner = recommendedPartnerQuery.data
  const { initialStep, currentStep } = useRecommendedPartnerSteps()
  const companyName = recommendedPartner.company.presentationName
  const logoUrl = recommendedPartner.company.logoUrl
  const scheduleCallUrl = toSafeHttpUrl(
    recommendedPartner.company.scheduleCompanyCallUrl,
  )

  useMount(() => {
    modal.setStep(initialStep)
  })

  return (
    <>
      <Helmet title={`Vetted Partner | ${companyName}`} />

      <DialogHeader
        className="relative flex flex-col items-start gap-2 px-5 py-6
          sm:flex-row sm:items-center sm:justify-between sm:gap-6"
      >
        <div className="hidden md:block" />

        <DialogTitle
          className="text-xl leading-[1.4] font-bold text-black sm:text-[26px]"
        >
          Introducing {companyName}
        </DialogTitle>

        <Image
          src={logoUrl || undefined}
          fallback={<div />}
          alt={companyName}
          className="mr-4 max-w-full text-[0px]"
          style={{ maxWidth: '120px', maxHeight: '60px' }}
        />

        <FontAwesomeIcon
          icon={faTimes}
          onClick={modal.hide}
          className="absolute top-0 right-0 sm:hidden"
          style={{ fontSize: '22px', padding: '10px 14px' }}
          role="button"
          aria-label="Close modal"
        />
      </DialogHeader>

      <DialogBody className="max-h-[80vh]">
        <RecommendedPartnerTabLinks />

        <div className="p-[28px_13px_10px] leading-[1.9] md:p-[28px_35px_10px]">
          {RECOMMENDED_PARTNER_STEPS[currentStep]}
        </div>
      </DialogBody>

      <DialogFooter>
        {scheduleCallUrl && (
          <Button
            variant="outline-secondary"
            className="w-full sm:w-auto"
            asChild
            size="lg"
          >
            <a href={scheduleCallUrl} target="_blank" rel="noreferrer">
              Schedule a call
            </a>
          </Button>
        )}

        <AddToReferralPartnersButton variant="primary" />
      </DialogFooter>
    </>
  )
}

export default RecommendedPartnerModal
